import { inject } from '@angular/core';
import { RedirectFunction } from '@angular/router';

import { defaultDashboardPath } from '../service/default-dashboard';
import { UserService } from '../service/user.service';

/** Entry screen for a visitor with no session. In production this bounces to `environment.LOGIN_URL`. */
const LOGIN_ENTRY_PATH = '/emailVerification';

/**
 * `redirectTo` for the two unauthenticated entry routes in {@link appRoutes} (`''` and `**`).
 *
 * Both previously redirected unconditionally to `/emailVerification`, which in production
 * hard-redirects to `environment.LOGIN_URL`. That logged out users whose session was
 * perfectly alive: coming back from the legacy portal after a Home / Change Password
 * handoff lands on the SPA's bare origin, and the unconditional redirect threw the live
 * session away before anything could read it.
 *
 * `sessionStorage` survives a same-tab cross-origin round trip, so `userToken` is still
 * present on the way back — only the route was blind to it. Checking it here preserves
 * the login bounce for genuinely signed-out visitors (a new tab starts with no
 * `userToken`, per {@link authGuard}) while sending a signed-in one to their dashboard.
 *
 * Runs in an injection context, so `inject()` is available.
 *
 * @returns Absolute path to redirect to: the user's default dashboard when a session
 *   token is present, otherwise the email-verification entry screen.
 */
export const entryRedirect: RedirectFunction = () => {
  const userService = inject(UserService);

  if (!sessionStorage.getItem('userToken')) {
    return LOGIN_ENTRY_PATH;
  }

  return defaultDashboardPath(userService);
};
